import { arrayMove } from "@dnd-kit/sortable";
// Mindset reminders, gratitude, habits and temptations: the lists Today's Reminders chooses from. They live on the
// user's compass document (users/{uid}/data/compass). `mindset` and `gratitude` are one list each; every category
// keeps its own `habits` and `traps` (temptations) (Craig, 24 Oct 2026: habits belong to a category, mindset
// reminders don't). Pure; tested beside this file. Each change returns a new list or categories array for setCompass.

import { COMPASS_KINDS } from "./compassKinds";

// An item is its words: a string, or an object with `text`. The old Compass page saved some as
// { text, deadline }; a habit whose user chose Build or Cut back is saved as { text, reduce }.
export const itemText = (item) => (typeof item === "string" ? item : (item?.text || ""));

// The guess, from whole words ("lesson", "execute" and "piano" build): ↓ for "Less phone after 9", "Stop snacking".
const REDUCE = /\b(less|fewer|reduce|stop|quit|cut|avoid|no|limit|don'?t|decrease)\b|↓/i;
export const guessReduce = (text) => REDUCE.test(text || "");

// Whether a habit is one you're cutting back (↓) rather than building (↑): the user's choice when they made one
// (Craig, 24 Oct 2026: "Allow the user to change the Build or Cut Back status if they feel the guess is incorrect"),
// otherwise the guess from its words.
export const isReduceHabit = (habit) => (typeof habit?.reduce === "boolean" ? habit.reduce : guessReduce(itemText(habit)));

// The stored habit with these words, from any category (Today's Reminders keeps only the words it chose). The same
// words in two categories with different arrows show the first one's.
// Every item's words in one kind's list ("mindset", "habits"…), from every category when it is grouped: what Today's
// Reminders offers. Words only: an old { text, deadline } item can't be drawn as it is.
export const kindTexts = (compass, kind) => {
  const { field, grouped } = COMPASS_KINDS[kind];
  const items = grouped ? (compass?.categories || []).flatMap(c => c[field] || []) : (compass?.[field] || []);
  return items.map(itemText);
};

export const findHabit = (categories, text) =>
  (categories || []).flatMap(c => c.habits || []).find(h => itemText(h) === text) || text;

const same = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();
const tidy = (item) => (typeof item === "string" ? item.trim() : { ...item, text: item.text.trim() });

// True when `text` is already in the list (ignoring case), other than at `exceptIndex`.
export const listHas = (list, text, exceptIndex = -1) =>
  (list || []).some((item, i) => i !== exceptIndex && same(itemText(item), text));

export const addToList = (list, item) => [...(list || []), tidy(item)];
export const editInList = (list, index, item) => (list || []).map((old, i) => (i === index ? tidy(item) : old));
export const removeFromList = (list, index) => (list || []).filter((_, i) => i !== index);
// Mindset or Gratitude after dragging the item in place `from` to place `to` (Craig, 29 Oct 2026: "I'd like drag
// handles on the Mindset reminders and the Gratitude reminders").
export const moveInList = (list, from, to) => arrayMove(list || [], from, to);

// The ids a list's rows drag by: each row's words, which are unique in a list (listHas, takenInCategory), so a moved row
// keeps its identity and focus; an old repeated item falls back to its place.
export const rowIds = (texts) => texts.map((text, i) => (texts.filter(t => t === text).length > 1 ? `#${i}` : `w:${text}`));

// The categories that have something in `field` ("habits" or "traps"), in the user's category order, each item
// with its words and its place in its list.
export const categoryGroups = (categories, field) => (categories || [])
  .filter(c => (c[field] || []).length > 0)
  .map(c => ({ category: c, items: c[field].map((item, index) => ({ item, text: itemText(item), index })) }));

const withList = (categories, field, id, change) => categories.map(c => (c.id === id ? { ...c, [field]: change(c[field] || []) } : c));

// The categories after saving an item in `field`. `from` is { categoryId, index } for one being edited, or null for
// a new one. One moved to another category goes to the end of that category's list.
export const saveInCategory = (categories, field, from, categoryId, item) => {
  if (!from) return withList(categories, field, categoryId, list => addToList(list, item));
  if (from.categoryId === categoryId) return withList(categories, field, categoryId, list => editInList(list, from.index, item));
  return withList(withList(categories, field, from.categoryId, list => removeFromList(list, from.index)), field, categoryId, list => addToList(list, item));
};

export const deleteInCategory = (categories, field, { categoryId, index }) => withList(categories, field, categoryId, list => removeFromList(list, index));

// The categories after dragging an item from place `from` to place `to` within its own category's list (Craig, 26 Oct
// 2026: "drag / drop sort my habits on the Habits page (obviously only within the category)").
export const moveInCategory = (categories, field, categoryId, from, to) => withList(categories, field, categoryId, list => arrayMove(list, from, to));

// Whether `text` would repeat an item already in the category it is being saved to.
export const takenInCategory = (categories, field, from, categoryId, text) => {
  const list = categories.find(c => c.id === categoryId)?.[field] || [];
  return listHas(list, text, from && from.categoryId === categoryId ? from.index : -1);
};

// How often a habit is meant to happen (Craig, 24 Oct 2026), in his order; stored as the id. A habit saved without
// one has none, and shows no pill.
export const HABIT_FREQUENCIES = [
  ["daily", "Daily"], ["alternate", "Every other day"], ["thriceweekly", "3 times a week"],
  ["weekly", "Weekly"], ["fortnightly", "Fortnightly"], ["monthly", "Monthly"],
];
export const frequencyLabel = (habit) => HABIT_FREQUENCIES.find(([id]) => id === habit?.frequency)?.[1] || null;

// A habit can serve one goal: the link is kept on the habit (`goalId`), like a Key Date's, and the Goal page finds
// its habits here (Craig, 24 Oct 2026: "add a new link / association between a habit and a goal"). Each comes with
// its category and place, so it can be edited where it's shown.
export const habitsForGoal = (categories, goalId) => (categories || []).flatMap(c =>
  (c.habits || []).map((item, index) => ({ item, text: itemText(item), categoryId: c.id, index }))
    .filter(h => goalId && h.item?.goalId === goalId));

// The categories with every habit's link to a deleted goal taken off.
export const unlinkGoalFromHabits = (categories, goalId) => (categories || []).map(c =>
  (c.habits || []).some(h => h?.goalId === goalId)
    ? { ...c, habits: c.habits.map(h => (h?.goalId === goalId ? (({ goalId: _gone, ...rest }) => rest)(h) : h)) }
    : c);

// A habit as the editor saves it: its words, ↑ or ↓, and its frequency and goal when it has them.
export const makeHabit = ({ text, reduce, frequency = null, goalId = null }) =>
  ({ text, reduce, ...(frequency ? { frequency } : {}), ...(goalId ? { goalId } : {}) });

// The habits a goal's picker offers: every habit not already for this goal, grouped by category in the user's order.
// One that serves another goal is offered too; choosing it moves it here, since a habit serves one goal.
export const habitsToLink = (categories, goalId) => categoryGroups(categories, "habits")
  .map(g => ({ ...g, items: g.items.filter(({ item }) => item?.goalId !== goalId) }))
  .filter(g => g.items.length > 0);

// The categories with one habit linked to `goalId`. A plain-words habit becomes an object, keeping its ↑ or ↓ guess.
// `text` is the habit's words when it was chosen: if the list has changed since, nothing is linked.
export const linkHabitToGoal = (categories, { categoryId, index, text }, goalId) => categories.map(c => (c.id !== categoryId || itemText(c.habits?.[index]) !== text ? c : {
  ...c,
  habits: c.habits.map((h, i) => (i !== index ? h : typeof h === "string" ? { text: h, reduce: guessReduce(h), goalId } : { ...h, goalId })),
}));
